import { useId } from "react";

import { cn } from "@perceptron/utils/cn";

import { BOUNDING_BOX } from "../../constants";
import type { DisplayBounds } from "../../utils/coordinates";
import type { VideoLayout } from "../../utils/videoLayout";

/**
 * Cut out current-frame boxes; an empty cutout list dims the whole video.
 *
 * Fades in on mount and back out when `exiting` flips, over the same 300ms the
 * image overlay's dim mask uses, so focus eases in and out on both media types
 * rather than the video snapping between dimmed and undimmed. `data-state`
 * reports which of the two it is doing.
 */
export function FocusMask({
  boxes,
  videoLayout,
  exiting = false,
}: {
  boxes: DisplayBounds[];
  videoLayout: VideoLayout;
  exiting?: boolean;
}) {
  const maskId = useId();
  const { width, height, offsetX, offsetY } = videoLayout;
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 z-[1] transition-opacity duration-300",
        exiting ? "opacity-0" : "animate-fade-in opacity-100",
      )}
      data-state={exiting ? "exiting" : "active"}
      data-video-focus-mask=""
      height={height + offsetY * 2}
      width={width + offsetX * 2}
    >
      <defs>
        <mask height={height} id={maskId} maskUnits="userSpaceOnUse" width={width} x={offsetX} y={offsetY}>
          <rect fill="white" height={height} width={width} x={offsetX} y={offsetY} />
          {boxes.map((box, index) => (
            <rect
              fill="black"
              height={Math.max(0, box.height)}
              key={index}
              rx={BOUNDING_BOX.CORNER_RADIUS}
              width={Math.max(0, box.width)}
              x={box.x}
              y={box.y}
            />
          ))}
        </mask>
      </defs>
      <rect
        fill="black"
        fillOpacity={0.4}
        height={height}
        mask={`url(#${maskId})`}
        width={width}
        x={offsetX}
        y={offsetY}
      />
    </svg>
  );
}
